/**
 * Entity Fetch Batcher — module-level singleton that batches entity fetch
 * requests across the component tree.
 *
 * When multiple components mount simultaneously (e.g., a list of action cards),
 * each one calls `ensureEntity(orgId, entityId)`. The batcher collects all
 * entity IDs over a 50ms window, groups them by orgId, fires one
 * `getByEntityIds` call per org, and upserts the results into the CRM store.
 *
 * Components read from the store via `useSyncExternalStore` — they don't
 * await the batcher. The batcher just ensures the store is populated.
 *
 * This replaces the old `useProposalEntityBatch` pattern (which required
 * a parent component to know all entity IDs upfront) with a self-sufficient
 * approach where each component requests what it needs.
 */

import { createCRMObjectClient } from '@/lib/crm/core/CRMObjectClient';
import {
  beginBatch,
  endBatch,
  getStoreGeneration,
  hasCompleteObject,
  registerEntityAlias,
  removeObject,
  upsertObject,
} from '@/lib/crm/store';
import { logger } from '@/lib/logger';

// ─── State ──────────────────────────────────────────────────────────────────

/** Pending entity IDs grouped by orgId, waiting for the next batch window. */
const pendingByOrg = new Map<number, Set<string>>();

/** Timer handle for the current batch window (per org). */
const timers = new Map<number, ReturnType<typeof setTimeout>>();

/**
 * Entity IDs currently being fetched, mapped to the store generation at which
 * the fetch was queued — used to dedupe concurrent requests for the same id.
 *
 * Keying by generation (not a bare set) is what keeps an org switch mid-flight
 * from stranding an id: a store clear bumps the generation, so an entry from an
 * earlier generation is a fetch the store-clear guard will discard. It must NOT
 * suppress a fresh request for the SAME id in the new org — otherwise the id is
 * never re-queued and its store entry stays unpopulated. Only requests at the
 * current generation dedupe against each other.
 */
const inflight = new Map<string, number>();

/**
 * Entity IDs whose most recent fetch attempt settled WITHOUT an object, split
 * by why — the two outcomes mean different things to a caller:
 *
 *   - `missingEntityIds`: the request succeeded and the server omitted the id.
 *     `by-entity-ids` silently skips ids that don't exist (deleted, bad
 *     reference) AND ids hidden from this user by access control, so
 *     "missing" is terminal for this user but is not proof of deletion.
 *   - `failedEntityIds`: the request itself rejected (network error, 5xx).
 *     Nothing is known about the entity; a retry may well succeed.
 *
 * Both are cleared when a retry is queued via `ensureEntity`. Observers
 * (e.g. useAssociations) subscribe to surface partial-failure UI without
 * having to own their own fetch pipeline.
 */
const missingEntityIds = new Set<string>();
const failedEntityIds = new Set<string>();

/** Subscribers notified when either settled-without-object set changes. */
const failureListeners = new Set<() => void>();

/**
 * Monotonic version counter — bumped every time the failure or missing set
 * changes, then broadcast to subscribers. React code reads this via
 * `getFailureVersion` as the snapshot for `useSyncExternalStore`.
 */
let failureVersion = 0;

function notifyFailureListeners(): void {
  failureVersion += 1;
  for (const listener of failureListeners) {
    listener();
  }
}

/** Batch window in milliseconds. */
const BATCH_WINDOW_MS = 50;

/** Maximum IDs per API request (backend memory limit on property prefetch). */
const MAX_CHUNK_SIZE = 100;

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Ensure an entity is in the CRM store. If the entity is already complete
 * in the store (or already being fetched), this is a no-op. Otherwise, it
 * queues the entity for batch fetching.
 *
 * Call this from hooks/effects — it's synchronous and non-blocking.
 */
export function ensureEntity(orgId: number, entityId: string): void {
  // Already complete in store — nothing to do
  if (hasCompleteObject(entityId)) {
    return;
  }

  // Already in-flight for the CURRENT org lifecycle — will be populated soon.
  // An entry from an earlier generation belongs to a pre-switch fetch that the
  // store-clear guard will discard, so it must not suppress this request; fall
  // through and re-queue the id for the new org.
  if (inflight.get(entityId) === getStoreGeneration()) {
    return;
  }

  // Re-queueing a previously-failed or previously-missing entity counts as
  // a retry: clear it so observers stop showing the error / missing UI.
  const wasFailed = failedEntityIds.delete(entityId);
  const wasMissing = missingEntityIds.delete(entityId);
  if (wasFailed || wasMissing) {
    notifyFailureListeners();
  }

  // Add to pending set for this org
  let pending = pendingByOrg.get(orgId);
  if (!pending) {
    pending = new Set();
    pendingByOrg.set(orgId, pending);
  }
  pending.add(entityId);

  // Schedule batch flush if not already scheduled
  if (!timers.has(orgId)) {
    timers.set(
      orgId,
      setTimeout(() => flushOrg(orgId), BATCH_WINDOW_MS)
    );
  }
}

/**
 * Subscribe to fetch failure notifications. Called whenever the set of
 * failed or missing entity IDs changes (a fetch settles without an object,
 * or a retry clears one). Returns an unsubscribe function.
 */
export function subscribeToFetchFailures(listener: () => void): () => void {
  failureListeners.add(listener);
  return () => {
    failureListeners.delete(listener);
  };
}

/**
 * Whether the most recent fetch attempt for an entity settled WITHOUT an
 * object — for either reason (request failed, or the server omitted the id).
 *
 * This is the "stop spinning, show the fallback" signal: it deliberately
 * covers both outcomes, because every consumer that reads it wants to know
 * "will this id arrive?" and the answer is no either way. Use
 * `hasMissingEntity` when the distinction matters.
 */
export function hasFailedFetch(entityId: string): boolean {
  return failedEntityIds.has(entityId) || missingEntityIds.has(entityId);
}

/**
 * Whether the most recent fetch for an entity SUCCEEDED and the server
 * omitted the id from the response — as opposed to the request failing.
 *
 * `by-entity-ids` skips ids that don't exist (deleted, bad reference) and ids
 * hidden from this user by access control, so this is "the record does not
 * exist for this user", not "the network was down". That is the difference
 * between a verdict a caller may act on (disable an action that references
 * a record that won't exist) and one it must fail open on.
 */
export function hasMissingEntity(entityId: string): boolean {
  return missingEntityIds.has(entityId);
}

/**
 * Test-only: whether an id is recorded in the internal failed-request set
 * specifically, as opposed to `hasFailedFetch`'s OR of both settled-without-
 * object sets. Used to assert the missing/failed sets stay mutually
 * exclusive within a single flush (an id already recorded as missing must
 * never also land in the failed set).
 */
export function __isInFailedSetForTesting(entityId: string): boolean {
  return failedEntityIds.has(entityId);
}

/**
 * Read the current failure version counter. Used as the snapshot value
 * for `useSyncExternalStore` so React components re-render whenever the
 * failure set changes (a fetch fails, or a retry clears one).
 */
export function getFailureVersion(): number {
  return failureVersion;
}

/**
 * Re-fetch store-backed entities after a WebSocket full-refresh recovery.
 *
 * The full-refresh path (`handleFullRefreshRequired` in useCrmWebSocket)
 * invalidates React Query caches, but objects hydrated directly into the CRM
 * store via `ensureEntity` (entity badges, object cards) have no React Query
 * observer, so invalidation alone never refreshes them — they would keep stale
 * or deleted data indefinitely after missed-event recovery.
 *
 * This re-fetches the given IDs in chunks (same policy as the normal fetch
 * path: 100/request, associations skipped) and:
 *   - upserts fresh server data over the stale store entries,
 *   - registers a merge alias when the server resolves a requested id to a
 *     canonical post-merge object,
 *   - removes IDs the server omits from an OTHERWISE SUCCESSFUL response —
 *     those entities were deleted or had access revoked while disconnected, so
 *     the stale store entry must go.
 *
 * Skipping associations does NOT drop association-derived card fields: the
 * response omits `associations` (undefined), and `upsertObject` merges it as
 * `obj.associations ?? existing.data.associations`, so any associations already
 * hydrated into the store entry are preserved across the refresh. Associations
 * are (re)hydrated with full detail when the user opens a slideout.
 *
 * A chunk whose request fails outright (network/5xx) leaves its entries
 * untouched: a transient error must not wipe visible data. Those entries stay
 * stale until the next refresh or WS event.
 *
 * This is fire-and-forget from the WS recovery path, so it can still be in
 * flight when the user switches orgs (which clears the store via `clearAll`).
 * To avoid writing previous-org results into the now-cleared store, we snapshot
 * the store generation before awaiting and drop the whole response if a clear
 * happened in the meantime.
 *
 * Unlike `ensureEntity`, this does NOT consult the `inflight` map or the
 * failed/missing sets, nor skip complete objects — refreshing is the whole
 * point, so it always fetches exactly the IDs it is handed.
 */
export async function refreshStoreEntities(
  orgId: number,
  entityIds: string[]
): Promise<void> {
  if (entityIds.length === 0) {
    return;
  }

  // Snapshot the store generation: if the store is wiped (org switch) while
  // our requests are in flight, the generation changes and we discard the
  // stale results instead of leaking them into the new org's store.
  const generationAtStart = getStoreGeneration();

  const client = createCRMObjectClient(orgId, 'ahoy_object');

  const chunks: string[][] = [];
  for (let i = 0; i < entityIds.length; i += MAX_CHUNK_SIZE) {
    chunks.push(entityIds.slice(i, i + MAX_CHUNK_SIZE));
  }

  // See flushOrg for why associations are skipped (high-degree entities are
  // extremely expensive to serialize).
  const results = await Promise.allSettled(
    chunks.map(chunk =>
      client.getByEntityIds(chunk, { includeAssociations: false })
    )
  );

  // The store was cleared (e.g. org switch) while we were awaiting — these
  // results belong to a previous org lifecycle. Dropping them prevents
  // cross-org stale data from being merged into the cleared store.
  if (getStoreGeneration() !== generationAtStart) {
    return;
  }

  beginBatch();
  try {
    for (let i = 0; i < results.length; i++) {
      const result = results[i];
      if (result.status !== 'fulfilled') {
        // Transient failure — leave this chunk's entries untouched so a
        // network blip doesn't wipe visible data.
        logger.error(
          'Store refresh chunk failed',
          result.reason instanceof Error
            ? result.reason
            : new Error(String(result.reason))
        );
        continue;
      }

      // Object.keys are the *requested* IDs (getByEntityIds keys its response
      // by request id, not the canonical post-merge id).
      const fetched = new Set<string>(Object.keys(result.value));
      for (const [requestedId, obj] of Object.entries(result.value)) {
        upsertObject(obj);
        if (requestedId !== obj.entity_id) {
          registerEntityAlias(requestedId, obj.entity_id);
        }
      }
      // IDs we asked for but the server didn't return in a successful response
      // were deleted or access-revoked while we were disconnected — drop the
      // stale store entry so badges/cards stop showing them.
      for (const id of chunks[i]) {
        if (!fetched.has(id)) {
          removeObject(id);
        }
      }
    }
  } finally {
    endBatch();
  }
}

// ─── Internal ───────────────────────────────────────────────────────────────

async function flushOrg(orgId: number): Promise<void> {
  timers.delete(orgId);
  const pending = pendingByOrg.get(orgId);
  pendingByOrg.delete(orgId);

  if (!pending || pending.size === 0) {
    return;
  }

  // Snapshot the store generation for this flush. It scopes both the `inflight`
  // entries we add below and the post-await guard that discards a cross-org
  // response. Captured before the first await, so it is stable across the whole
  // synchronous prologue (filter → chunk → fetch dispatch have no awaits).
  const generationAtStart = getStoreGeneration();

  // Filter out any that became complete while waiting (e.g., slideout opened)
  // or are already in flight FOR THIS generation. An in-flight entry from an
  // earlier generation is a pre-switch fetch that will be discarded, so it must
  // not block re-fetching the id for the current org.
  const toFetch: string[] = [];
  for (const id of pending) {
    if (!hasCompleteObject(id) && inflight.get(id) !== generationAtStart) {
      toFetch.push(id);
      inflight.set(id, generationAtStart);
    }
  }

  if (toFetch.length === 0) {
    return;
  }

  // Ids the server actually returned before any exception. The outer catch is a
  // fallback for ids that never got an outcome — it must not re-mark these
  // successfully-fetched ids as failed if a later chunk throws while processing.
  const fetchedIds = new Set<string>();

  try {
    const client = createCRMObjectClient(orgId, 'ahoy_object');

    // Chunk to respect the API's per-request ID limit
    const chunks: string[][] = [];
    for (let i = 0; i < toFetch.length; i += MAX_CHUNK_SIZE) {
      chunks.push(toFetch.slice(i, i + MAX_CHUNK_SIZE));
    }

    // Fetch all chunks in parallel — use allSettled so partial success
    // still upserts available entities instead of dropping everything.
    //
    // Skip associations: the batcher hydrates entity badges and object
    // cards that only need display properties (name, email, type). The
    // backend's `compute_accessible_association_targets` is extremely
    // expensive for high-degree entities (15k+ associations on active
    // users) — 7+ seconds of server time per batch. Associations are
    // fetched separately when the user opens a slideout.
    const results = await Promise.allSettled(
      chunks.map(chunk =>
        client.getByEntityIds(chunk, { includeAssociations: false })
      )
    );

    // The store was cleared (org switch) while we were awaiting — discard the
    // whole batch so previous-org results never leak into the new org's store
    // or its missing/failed sets.
    if (getStoreGeneration() !== generationAtStart) {
      return;
    }

    beginBatch();
    let failureDelta = false;
    try {
      for (let i = 0; i < results.length; i++) {
        const result = results[i];
        if (result.status === 'fulfilled') {
          // Use Object.keys (the *requested* entity IDs) to track
          // successful fetches — not obj.entity_id, which is the canonical
          // post-merge ID and may differ from the requested ID for merged
          // entities. getByEntityIds keys its response by the requested ID.
          const fetched = new Set<string>(Object.keys(result.value));
          for (const [requestedId, obj] of Object.entries(result.value)) {
            upsertObject(obj);
            fetchedIds.add(requestedId);
            // When the server resolves a pre-merge request to a canonical
            // post-merge object, register the alias so store consumers that
            // look up the requested (pre-merge) id can find the canonical
            // entry. Without this, merged association targets never render.
            if (requestedId !== obj.entity_id) {
              registerEntityAlias(requestedId, obj.entity_id);
            }
          }
          // IDs we asked for that a SUCCESSFUL response didn't return are
          // missing for this user (deleted, bad reference, or hidden by
          // access control) — recorded separately from a rejected request so
          // observers can tell "won't exist" from "couldn't check".
          for (const id of chunks[i]) {
            if (!fetched.has(id) && !missingEntityIds.has(id)) {
              missingEntityIds.add(id);
              failureDelta = true;
            }
          }
        } else {
          logger.error(
            'Entity batch chunk failed',
            result.reason instanceof Error
              ? result.reason
              : new Error(String(result.reason))
          );
          for (const id of chunks[i]) {
            if (!failedEntityIds.has(id)) {
              failedEntityIds.add(id);
              failureDelta = true;
            }
          }
        }
      }
    } finally {
      endBatch();
    }
    if (failureDelta) {
      notifyFailureListeners();
    }
  } catch (error) {
    // Log but don't throw — components will show fallback data
    logger.error(
      'Entity batch fetch failed',
      error instanceof Error ? error : new Error(String(error))
    );
    let failureDelta = false;
    for (const id of toFetch) {
      // The outer handler's job is only to mark ids that never got ANY outcome
      // before the throw. Skip ids the server returned (`fetchedIds`) and ids a
      // successful response omitted (`missingEntityIds`): both already had their
      // correct, more-specific outcome recorded and must not be re-marked failed.
      if (
        !failedEntityIds.has(id) &&
        !missingEntityIds.has(id) &&
        !fetchedIds.has(id)
      ) {
        failedEntityIds.add(id);
        failureDelta = true;
      }
    }
    if (failureDelta) {
      notifyFailureListeners();
    }
  } finally {
    // Release only the in-flight entries this flush owns. If an org switch
    // re-queued an id at a later generation while we awaited, that newer fetch
    // now owns the entry — deleting it here would un-dedupe the new org's
    // in-flight request.
    for (const id of toFetch) {
      if (inflight.get(id) === generationAtStart) {
        inflight.delete(id);
      }
    }
  }
}
